import { useCallback } from 'react';

// Define constants for layout spacing
const LEVEL_SPACING_Y = 200;
const LEVEL_SPACING_X = 350;

const useNodeBuilder = (createNodeLabel, isMobile) => {
  const createNodesFromData = useCallback(
    (data) => {
      if (!Array.isArray(data)) {
        console.error('Invalid data format for nodes:', data);
        return [];
      }

      const nodes = [];
      const queue = [];
      let currentY = 0;
      let currentX = 500;

      const topicNode = data.find((item) => item.header === 'topic name');
      if (topicNode) {
        nodes.push({
          id: `${topicNode.id}`,
          data: {
            label: createNodeLabel(
              topicNode.header,
              topicNode.content,
              topicNode.url_sources,
              topicNode.id,
              data.some((item) => item.parent_id === topicNode.id),
              topicNode.type,
              topicNode.images
            ),
            originalHeader: topicNode.header,
            originalContent: topicNode.content,
            urlSources: topicNode.url_sources,
            type: topicNode.type,
            images: topicNode.images,
          },
          position: topicNode.position || { x: currentX, y: currentY },
          style: {
            background: '#141414',
            color: '#fff',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            width: 320,
          },
        });

        queue.push({ nodeId: topicNode.id, currentY: currentY + LEVEL_SPACING_Y });
      }

      while (queue.length > 0) {
        const { nodeId, currentY } = queue.shift();
        const parentNode = nodes.find((n) => n.id === `${nodeId}`);
        const childNodes = data.filter((item) => item.parent_id === nodeId);
        const totalWidth = (childNodes.length - 1) * LEVEL_SPACING_X;
        const startX = parentNode.position.x - totalWidth / 2;

        childNodes.forEach((child, index) => {
          const hasChildNodes = data.some((item) => item.parent_id === child.id);
          const childNode = {
            id: `${child.id}`,
            data: {
              label: createNodeLabel(
                child.header,
                child.content,
                child.url_sources,
                child.id,
                hasChildNodes,
                child.type,
                child.images
              ),
              originalHeader: child.header,
              originalContent: child.content,
              urlSources: child.url_sources,
              type: child.type,
              images: child.images,
            },
            position: child.position || { x: startX + index * LEVEL_SPACING_X, y: currentY },
            parentNode: `${nodeId}`,
            style: {
              background: 'rgba(20, 20, 20)',
              color: 'rgba(255, 255, 255, 0.7)',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: '12px',
              width: isMobile ? 250 : 320,
            },
          };

          nodes.push(childNode);

          if (hasChildNodes) {
            queue.push({ nodeId: child.id, currentY: currentY + LEVEL_SPACING_Y });
          }
        });
      }

      return nodes;
    },
    [createNodeLabel, isMobile]
  );

  const createEdgesFromData = useCallback((data) => {
    return data
      .filter((item) => item.parent_id !== 0)
      .map((item) => ({
        id: `e-${item.parent_id}-${item.id}`,
        source: `${item.parent_id}`,
        target: `${item.id}`,
        animated: false,
        style: {
          stroke: '#707070',
          strokeWidth: 1,
        },
      }));
  }, []);

  const createHierarchicalLayout = useCallback(
    (data, parentNode) => {
      const nodes = [];
      const edges = [];
      const descriptions = [];
      const keyEvents = [];
      const subtopics = [];

      data.forEach((item) => {
        if (item.type === 'description') descriptions.push(item);
        else if (item.type === 'key_event') keyEvents.push(item);
        else if (item.type === 'subtopic') subtopics.push(item);
      });

      const createNodesForType = (items, yOffset) => {
        if (items.length === 0) return [];

        const totalWidth = (items.length - 1) * LEVEL_SPACING_X;
        const startX = parentNode.position.x - totalWidth / 2;

        return items.map((item, index) => {
          const nodeId = `${parentNode.id}-${item.type}-${item.id}`;
          const node = {
            id: nodeId,
            type: 'default',
            data: {
              label: createNodeLabel(
                item.header,
                item.content,
                item.url_sources,
                nodeId,
                false,
                item.type,
                item.images
              ),
              originalHeader: item.header,
              originalContent: item.content,
              urlSources: item.url_sources,
              type: item.type,
              images: item.images,
            },
            position: {
              x: startX + index * LEVEL_SPACING_X,
              y: parentNode.position.y + yOffset,
            },
            parentNode: parentNode.id,
            style: {
              background: 'rgba(20, 20, 20)',
              color: 'rgba(255, 255, 255, 0.7)',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: '12px',
              width: 320,
            },
            hidden: false,
          };

          edges.push({
            id: `e-${parentNode.id}-${nodeId}`,
            source: parentNode.id,
            target: nodeId,
            type: 'default',
            style: {
              stroke: '#E5E5E5',
              strokeWidth: 1,
            },
          });

          return node;
        });
      };

      nodes.push(
        ...createNodesForType(descriptions, LEVEL_SPACING_Y),
        ...createNodesForType(keyEvents, LEVEL_SPACING_Y * 4),
        ...createNodesForType(subtopics, LEVEL_SPACING_Y * 4)
      );

      return { nodes, edges };
    },
    [createNodeLabel]
  );

  return { createNodesFromData, createEdgesFromData, createHierarchicalLayout };
};

export default useNodeBuilder;
